// Per-chat debug bundle: everything needed to reproduce or diagnose a chat
// turn, as one JSON file to attach to a bug report.
//
// The server record (GET /chats/{id}) is the source of truth — it carries
// the plan, routing, step timings, sources, IOCs and graph traversal of
// every turn exactly as persisted. Temporary chats never reach the server,
// so for those we fall back to the messages held in the browser.

import type { ChatDetail, TurnRecord } from './feedbackApi';
import type { ChatSession, StoredMessage } from '../types';

export const DEBUG_BUNDLE_SCHEMA = 'malpediarag.chat-debug/1';

const OPTIONS_KEY = 'malpedia-chat-options';

interface TimedStep {
  step: string;
  message?: string;
  count?: number;
  ts: number;
  kind?: string;
  model?: string;
  service?: string;
  phase?: string;
  ttft_s?: number;
  duration_s?: number;
  tokens?: number;
  tokens_estimated?: boolean;
  tok_s?: number;
  llm_s?: number;
  tool_s?: number;
}

export interface StepTiming {
  step: string;
  message?: string;
  count?: number;
  /** Seconds since the first progress event of the turn. */
  at_s: number;
  /** Seconds until the next progress event (null for the last one). */
  duration_s: number | null;
  /** Where the step's time goes (llm / remote / local), when the server said. */
  kind?: string;
  model?: string;
  service?: string;
  phase?: string;
}

/** The answer's split, from its first-token and done events. */
export interface AnswerTiming {
  model: string | null;
  ttft_s: number | null;
  duration_s: number | null;
  tokens: number | null;
  tokens_estimated: boolean | null;
  tok_s: number | null;
}

/** Seconds per kind of work, summed over the gaps between progress events. */
export interface TimeSplit {
  llm_s: number;
  remote_s: number;
  local_s: number;
}

export interface TurnSummary {
  seq: number;
  query: string;
  answer_chars: number;
  source_count: number;
  model_name: string | null;
  latency_ms: number | null;
  routing_mode: string | null;
  strategies: string[];
  timeline: StepTiming[];
  /** Absent for turns stored before the server sent timing tags. */
  time_split?: TimeSplit;
  answer?: AnswerTiming;
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function isTimedStep(v: unknown): v is TimedStep {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as TimedStep).step === 'string' &&
    typeof (v as TimedStep).ts === 'number'
  );
}

/** Turn raw progress events into relative timings (pure; exported for tests). */
export function stepTimeline(details: unknown[] | null | undefined): StepTiming[] {
  const steps = (details ?? []).filter(isTimedStep);
  if (steps.length === 0) return [];
  const t0 = steps[0].ts;
  return steps.map((s, i) => ({
    step: s.step,
    message: s.message,
    count: s.count,
    at_s: round(s.ts - t0),
    duration_s: i + 1 < steps.length ? round(steps[i + 1].ts - s.ts) : null,
    ...(s.kind ? { kind: s.kind } : {}),
    ...(s.model ? { model: s.model } : {}),
    ...(s.service ? { service: s.service } : {}),
    ...(s.phase ? { phase: s.phase } : {}),
  }));
}

/** LLM / remote / local seconds of a timed turn: each gap between two
 *  progress events counts for the kind of the step running in it (its
 *  tagged events; LLM wins), a gap after a step's done event as local.
 *  Undefined for untagged (older) turns. */
export function timeSplit(details: unknown[] | null | undefined): TimeSplit | undefined {
  const steps = (details ?? []).filter(isTimedStep);
  if (!steps.some((s) => s.kind)) return undefined;
  const kindOf: Record<string, string> = {};
  const out: TimeSplit = { llm_s: 0, remote_s: 0, local_s: 0 };
  for (let i = 0; i + 1 < steps.length; i++) {
    const s = steps[i];
    if (s.kind && (kindOf[s.step] !== 'llm' || s.kind === 'llm')) kindOf[s.step] = s.kind;
    const gap = steps[i + 1].ts - s.ts;
    const k = s.step === 'strategy_done' || s.phase === 'done' ? 'local' : kindOf[s.step] ?? 'local';
    if (k === 'llm') out.llm_s += gap;
    else if (k === 'remote') out.remote_s += gap;
    else out.local_s += gap;
  }
  return { llm_s: round(out.llm_s), remote_s: round(out.remote_s), local_s: round(out.local_s) };
}

/** The answer's time to first token, duration and output rate. */
export function answerTiming(details: unknown[] | null | undefined): AnswerTiming | undefined {
  const gen = (details ?? []).filter(isTimedStep).filter((s) => s.step === 'generating');
  const done = [...gen].reverse().find((s) => s.phase === 'done');
  const first = gen.find((s) => s.phase === 'first_token');
  if (!done && !first) return undefined;
  return {
    model: done?.model ?? first?.model ?? null,
    ttft_s: done?.ttft_s ?? first?.ttft_s ?? null,
    duration_s: done?.duration_s ?? null,
    tokens: done?.tokens ?? null,
    tokens_estimated: done?.tokens_estimated ?? null,
    tok_s: done?.tok_s ?? null,
  };
}

function timingFields(details: unknown[] | null | undefined): Pick<TurnSummary, 'time_split' | 'answer'> {
  const split = timeSplit(details);
  const answer = answerTiming(details);
  return { ...(split ? { time_split: split } : {}), ...(answer ? { answer } : {}) };
}

function planStrategies(plan: Record<string, unknown> | null | undefined): string[] {
  const specs = plan?.strategies;
  if (!Array.isArray(specs)) return [];
  return specs
    .map((s) => (typeof s === 'string' ? s : (s as { name?: unknown })?.name))
    .filter((s): s is string => typeof s === 'string');
}

function summarizeServerTurn(t: TurnRecord): TurnSummary {
  return {
    seq: t.seq,
    query: t.query,
    answer_chars: t.answer?.length ?? 0,
    source_count: t.sources?.length ?? 0,
    model_name: t.model_name,
    latency_ms: t.latency_ms,
    routing_mode: (t.routing_event?.mode as string | undefined) ?? null,
    strategies: planStrategies(t.plan ?? t.planning_event),
    timeline: stepTimeline(t.step_details?.step_details),
    ...timingFields(t.step_details?.step_details),
  };
}

function summarizeLocalTurns(messages: StoredMessage[]): TurnSummary[] {
  const out: TurnSummary[] = [];
  let query = '';
  for (const m of messages) {
    if (m.role === 'user') {
      query = m.content;
      continue;
    }
    out.push({
      seq: out.length + 1,
      query,
      answer_chars: m.content.length,
      source_count: m.sources?.length ?? 0,
      model_name: null,
      latency_ms: null,
      routing_mode: m.routingEvent?.mode ?? null,
      strategies: planStrategies(m.planningEvent as unknown as Record<string, unknown> | null),
      timeline: stepTimeline(m.stepDetails),
      ...timingFields(m.stepDetails),
    });
  }
  return out;
}

function readChatOptions(): unknown {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function buildChatDebugBundle(
  session: ChatSession,
  detail: ChatDetail | null,
  fetchError?: string,
): Record<string, unknown> {
  return {
    schema: DEBUG_BUNDLE_SCHEMA,
    exported_at: new Date().toISOString(),
    client: {
      url: window.location.href,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      build_mode: import.meta.env.MODE,
      chat_options: readChatOptions(),
    },
    chat: {
      session_id: session.id,
      server_chat_id: session.serverChatId ?? null,
      title: session.title,
      temporary: !!session.temporary,
      is_sensitive: detail?.is_sensitive ?? session.serverIsSensitive ?? false,
      folder_id: session.folderId ?? null,
      created_at: detail?.created_at ?? new Date(session.createdAt).toISOString(),
      updated_at: detail?.updated_at ?? new Date(session.updatedAt).toISOString(),
    },
    source: detail ? 'server' : 'browser',
    server_fetch_error: fetchError ?? null,
    summary: detail ? detail.turns.map(summarizeServerTurn) : summarizeLocalTurns(session.messages),
    // Raw records last: they're large, and the summary is what a reader
    // looks at first.
    turns: detail ? detail.turns : session.messages,
    accumulated_graph: detail ? detail.accumulated_graph : session.accumulatedGraph ?? null,
  };
}
